import { Button } from '@/components/ui/button'
import {
  getDepartmentTalentCoverage,
  getInternalMobilityReadiness,
} from '@/services/internal-mobility.service'
import { useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  FileText,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

type StepTone = 'complete' | 'attention' | 'waiting'

interface ReadinessStep {
  label: string
  detail: string
  action: string
  href: string
  tone: StepTone
  icon: typeof Building2
}

const toneStyles: Record<StepTone, string> = {
  complete: 'border-emerald-200 bg-emerald-50/70 text-emerald-900',
  attention: 'border-amber-200 bg-amber-50/80 text-amber-950',
  waiting: 'border-blue-200 bg-blue-50/80 text-blue-950',
}

export function InternalMobilityReadinessBanner() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const readinessQuery = useQuery({
    queryKey: ['internal-mobility', 'readiness'],
    queryFn: () => getInternalMobilityReadiness(),
    staleTime: 30_000,
  })
  const readiness = readinessQuery.data
  const coverageQuery = useQuery({
    queryKey: ['internal-mobility', 'readiness', 'coverage'],
    queryFn: () =>
      getDepartmentTalentCoverage({
        departmentPage: 1,
        departmentLimit: 1,
        candidatePage: 1,
        candidateLimit: 1,
      }),
    enabled: Boolean(readiness?.structure_ready && readiness.mapped_open_role_count > 0),
    staleTime: 30_000,
  })

  const coverage = coverageQuery.data?.summary
  const evidenceIssues = readiness
    ? readiness.missing_resume_count +
      readiness.missing_performance_review_count +
      readiness.documents_processing_count +
      readiness.documents_failed_count
    : 0
  const matrixProcessing = ['queued', 'running'].includes(readiness?.latest_matrix_status ?? '')
  const steps: ReadinessStep[] = readiness
    ? [
        {
          label: 'Organization structure',
          detail: readiness.structure_ready
            ? `${readiness.department_count} departments published`
            : readiness.chart_status === 'missing'
              ? 'Organization chart required'
              : 'Chart review or publishing required',
          action: readiness.structure_ready ? 'Review chart' : 'Finish setup',
          href: '/internal-mobility/organization-chart',
          tone: readiness.structure_ready ? 'complete' : 'attention',
          icon: Building2,
        },
        {
          label: 'Internal job demand',
          detail:
            readiness.unmapped_open_role_count > 0
              ? `${readiness.unmapped_open_role_count} open ${readiness.unmapped_open_role_count === 1 ? 'role needs' : 'roles need'} mapping`
              : readiness.mapped_open_role_count > 0
                ? `${readiness.mapped_open_role_count} open roles mapped`
                : 'No mapped open roles',
          action: readiness.unmapped_open_role_count > 0 ? 'Resolve mappings' : 'Review jobs',
          href: '/internal-jobs',
          tone:
            readiness.unmapped_open_role_count > 0 || readiness.mapped_open_role_count === 0
              ? 'attention'
              : 'complete',
          icon: BriefcaseBusiness,
        },
        {
          label: 'Candidate evidence',
          detail:
            readiness.enrolled_candidate_count === 0
              ? 'No candidates enrolled'
              : evidenceIssues > 0
                ? `${readiness.ranking_ready_candidate_count} of ${readiness.enrolled_candidate_count} ready`
                : `${readiness.enrolled_candidate_count} candidates ready`,
          action: evidenceIssues > 0 ? 'Review documents' : 'Review candidates',
          href: '/internal-mobility/candidates',
          tone:
            readiness.enrolled_candidate_count > 0 && evidenceIssues === 0
              ? 'complete'
              : 'attention',
          icon: FileText,
        },
        {
          label: 'Match results',
          detail: matrixProcessing
            ? 'Generation in progress'
            : coverage
              ? coverage.stale_candidate_count > 0
                ? `${coverage.current_candidate_count} current · ${coverage.stale_candidate_count} need refresh`
                : `${coverage.current_candidate_count} current results`
              : 'Generate candidate matches',
          action:
            matrixProcessing || (coverage?.stale_candidate_count ?? 0) > 0
              ? 'Open and refresh'
              : 'Open matrix',
          href: '/internal-mobility/matrix',
          tone:
            !matrixProcessing &&
            Boolean(coverage) &&
            coverage?.stale_candidate_count === 0 &&
            coverage.current_candidate_count > 0
              ? 'complete'
              : matrixProcessing
                ? 'waiting'
                : 'attention',
          icon:
            matrixProcessing || (coverage?.stale_candidate_count ?? 0) > 0 ? RefreshCw : Sparkles,
        },
      ]
    : []
  const actionCount = steps.filter((step) => step.tone === 'attention').length
  const hasWaiting = steps.some((step) => step.tone === 'waiting')
  const allReady = steps.length > 0 && steps.every((step) => step.tone === 'complete')
  const triggerLabel = allReady
    ? 'Ready'
    : actionCount > 0
      ? `Readiness · ${actionCount} actions`
      : hasWaiting
        ? 'Readiness · Processing'
        : 'Readiness'
  const isOrganizationChart = location.pathname.startsWith('/internal-mobility/organization-chart')

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-label={triggerLabel}
        onClick={() => setOpen(true)}
        className={`absolute right-4 z-30 h-11 w-11 rounded-full p-0 shadow-lg transition-transform hover:-translate-y-0.5 sm:right-6 sm:h-9 sm:w-auto sm:px-4 ${isOrganizationChart ? 'bottom-4 md:bottom-20' : 'bottom-4 sm:bottom-6'} ${allReady ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : actionCount > 0 ? 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100' : 'bg-white'}`}
      >
        {readinessQuery.isLoading || coverageQuery.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : hasWaiting ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : allReady ? (
          <CheckCircle2 className="h-4 w-4" />
        ) : (
          <AlertCircle className="h-4 w-4" />
        )}
        <span className="hidden sm:inline">{triggerLabel}</span>
        {actionCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-600 px-1 text-[10px] font-bold text-white sm:hidden">
            {actionCount}
          </span>
        )}
      </Button>

      <aside
        aria-label="Internal Mobility readiness panel"
        aria-hidden={!open}
        className={`absolute inset-y-0 right-0 z-40 flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-start justify-between border-b px-5 py-5">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Internal Mobility readiness</h2>
            <p className="mt-1 text-sm text-slate-500">
              Review the information needed for reliable internal talent decisions.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close readiness panel"
            onClick={() => setOpen(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {readinessQuery.isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-teal-700" />
          </div>
        ) : readinessQuery.isError || !readiness ? (
          <div className="m-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            Readiness information could not be loaded.
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full"
              onClick={() => readinessQuery.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <div className="mb-5 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-950">
                  {allReady
                    ? 'Ready for talent decisions'
                    : `${actionCount} actions need attention`}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {steps.filter((step) => step.tone === 'complete').length} of {steps.length} areas
                  ready
                </p>
              </div>
              {allReady ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              ) : (
                <AlertCircle className="h-6 w-6 text-amber-600" />
              )}
            </div>

            <div className="space-y-3">
              {steps.map((step) => {
                const Icon = step.icon
                return (
                  <Link
                    key={step.label}
                    to={step.href}
                    onClick={() => setOpen(false)}
                    className={`group block rounded-xl border p-4 transition hover:shadow-sm ${toneStyles[step.tone]}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/70">
                        <Icon
                          className={`h-4 w-4 ${matrixProcessing && step.label === 'Match results' ? 'animate-spin' : ''}`}
                        />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold">{step.label}</p>
                          {step.tone === 'complete' && <CheckCircle2 className="h-4 w-4" />}
                        </div>
                        <p className="mt-1 text-xs opacity-75">{step.detail}</p>
                        <p className="mt-3 inline-flex items-center gap-1 text-xs font-semibold">
                          {step.action}
                          <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                        </p>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </aside>
    </>
  )
}
